import WithUser, { WithUserProps } from '@HOCs/withUser';
import { AlertModal, Banner, Flex, ProfileInfoCard, Text } from '@components';
import { FormattedMessage, useIntl } from 'react-intl';
import { ContentContainer } from './InvestmentProfileInfo.styles';
import {
  getBankDetails,
  getPersonalInfo,
  getQuestionnaireAnswers,
  getTaxDetails,
} from './InvestmentProfileInfo.utils';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { Steps as ProgressSteps } from '../Progress/Progress.types';
import { useUserTransactionsQuery } from '@features/api/userPortfolioSlice';
import { useEffect, useState } from 'react';
import { InvestmentContainer } from '../Investment.styles';
import {
  useGetCompletedQuestionnaireQuery,
  useGetQuestionnaireTemplateQuery,
} from '@features/api/questionnaireSlice';
import { QuestionnaireFieldType } from '@features/api/response/questionnaire';

const InvestmentProfileInfo: React.FC<WithUserProps> = ({ user }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { productId } = useParams();
  const intl = useIntl();

  const { data: questionnaire } = useGetQuestionnaireTemplateQuery();
  const { data: completedQuestionnaire } = useGetCompletedQuestionnaireQuery(
    undefined,
    {
      skip: !user.completedQuestionnaire,
    }
  );

  const [questionnaireData, setQuestionnaireDataData] = useState<{
    [key: string]: string[] | string;
  }>({});
  const { data: transactions } = useUserTransactionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [openModal, setOpenModal] = useState<boolean>(false);

  useEffect(() => {
    if (completedQuestionnaire && questionnaire) {
      const answers = completedQuestionnaire.questionnaireFieldAnswers.reduce(
        (result: { [key: string]: string[] | string }, current) => {
          const fieldTemplate = questionnaire.fields.find(
            (x) => x.id === current.fieldId
          );

          result[current.fieldId] =
            fieldTemplate?.type === QuestionnaireFieldType.MultipleSelect
              ? current.answers
              : current.answers[0];

          return result;
        },
        {}
      );

      setQuestionnaireDataData(answers);
    }
  }, [completedQuestionnaire, questionnaire]);

  const onAlertModalSubmit = async () => {
    navigate(`/investment-subscription/${productId}/progress`, {
      state: {
        completedSteps: [ProgressSteps.Details],
        progress: 7,
      },
      replace: true,
    });
  };

  const handleNextButtonNavigation = (): void => {
    if (user.personalDetailsLocked && transactions?.length) {
      navigate(`/investment-subscription/${productId}/contract-signature`, {
        state: {
          progress: 9,
          productId: productId,
        },
        replace: true,
      });
    } else {
      setOpenModal(true);
    }
  };

  const handlePreviousButtonNavigation = (): void => {
    if (user.personalDetailsLocked) {
      navigate(`/investment-subscription/${productId}/amount`, {
        state: {
          progress: 0,
        },
      });
    } else {
      navigate(`/investment-subscription/${productId}/questionnaire`, {
        preventScrollReset: false,
        state: {
          progress: 6,
        },
        replace: true,
      });
    }
  };

  const editPersonalInfo = (): void => {
    navigate(`/investment-subscription/${productId}/personal-details`, {
      state: {
        progress: 2,
        isEdit: true,
      },
      replace: true,
    });
  };

  const editTaxDetail = (): void => {
    navigate(`/investment-subscription/${productId}/tax-details`, {
      state: {
        progress: 4,
        isEdit: true,
      },
      replace: true,
    });
  };

  const editBankDetail = (): void => {
    navigate(`/investment-subscription/${productId}/bank-details`, {
      preventScrollReset: false,
      state: {
        progress: 5,
        isEdit: true,
      },
      replace: true,
    });
  };

  const editQuestionnaire = (): void => {
    navigate(`/investment-subscription/${productId}/questionnaire`, {
      preventScrollReset: false,
      state: {
        progress: 6,
        isEdit: true,
      },
      replace: true,
    });
  };

  return (
    <InvestmentContainer>
      <ContentContainer>
        {user.personalDetailsLocked ? (
          <Flex flex={1}>
            <Banner color='warning'>
              <Flex paddingY={6}>
                <Text
                  variant='h6'
                  fontWeight='fontWeightMedium'
                  color={theme.palette.text.primary}
                >
                  <FormattedMessage
                    id='investment.contactSupportProfileInfo'
                    values={{
                      bold: (chunks: React.ReactNode[]): React.ReactNode => (
                        <Text
                          variant='h6'
                          fontWeight='fontWeightBold'
                          display='inline-flex'
                        >
                          {chunks}
                        </Text>
                      ),
                    }}
                  />
                </Text>
              </Flex>
            </Banner>
          </Flex>
        ) : (
          <Text
            variant='h3'
            fontWeight='fontWeightSemiBold'
            color={theme.palette.text.primary}
          >
            <FormattedMessage id='investment.reviewYourDetails' />
          </Text>
        )}
        <ProfileInfoCard
          title={intl.formatMessage({
            id: 'investment.progress.personalDetails',
          })}
          data={getPersonalInfo(user)}
          rightButtonTestId='personal-details-edit'
          hideRightButton={user.personalDetailsLocked}
          onRightButtonPress={editPersonalInfo}
        />
        <ProfileInfoCard
          title={intl.formatMessage({ id: 'investment.taxDetails.taxDetails' })}
          data={getTaxDetails(user)}
          rightButtonTestId='tax-details-edit'
          hideRightButton={user.personalDetailsLocked}
          onRightButtonPress={editTaxDetail}
        />
        <ProfileInfoCard
          title={intl.formatMessage({
            id: 'investment.bankDetails.bankDetails',
          })}
          data={getBankDetails(user)}
          rightButtonTestId='bank-details-edit'
          hideRightButton={user.personalDetailsLocked}
          onRightButtonPress={editBankDetail}
        />
        <ProfileInfoCard
          title={intl.formatMessage({
            id: 'investment.questionnaire.complianceDetails',
          })}
          data={getQuestionnaireAnswers(questionnaire, questionnaireData)}
          rightButtonTestId='compliance-details-edit'
          hideRightButton={user.personalDetailsLocked}
          onRightButtonPress={editQuestionnaire}
        />
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: handlePreviousButtonNavigation,
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          onClick: handleNextButtonNavigation,
        }}
      />
      <AlertModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={intl.formatMessage({ id: 'misc.warning' })}
        description={intl.formatMessage({
          id: 'investment.profileLockMessage',
        })}
        buttonProps={{
          title: intl.formatMessage({ id: 'misc.next' }),
          onClick: onAlertModalSubmit,
        }}
        secondaryButtonProps={{
          title: intl.formatMessage({ id: 'misc.cancel' }),
          onClick: () => setOpenModal(false),
        }}
      />
    </InvestmentContainer>
  );
};

export default WithUser(InvestmentProfileInfo);
